import React, { useState } from 'react';
import {
  Button,
  ButtonGroup,
  // Select,
  // MenuItem,
  FormControl,
  // InputLabel,
  // SelectChangeEvent,
  TextField,
  Paper,
  Grid,
} from '@mui/material';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { CircularProgress } from '@mui/material';
import { ReactComponent as Excel } from '../../assets/ICONS/excel.svg';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import './Header.css';
import { SelectedAccount } from '../SelectAccount/SelectAccount';
import { Autocomplete } from '@material-ui/lab';
import { CLOUD_PROVIDER_SERVICES } from '../../utils/constants';
import { awsRegions, azureLocations } from '../../pages/ServicesReports/config';
import { useSelector } from 'react-redux';
import { accountState } from '../../store';
import { Download } from '@mui/icons-material';
import CustomTooltip from '../MasterComponent/CustomTooltip';

// Define types for our component props and state
type TimeFrameDisplay = 'Daily' | 'Weekly' | 'Monthly';
// | 'Custom';
export type TimeFrameAPI = 'day' | 'week' | 'month';
// | 'custom';
// | 'Custom';
type Region = string;
type Service = string;

interface ServiceReportHeaderProps {
  downloadCsvHandler?: () => void;
  // onViewChange?: (view: 'table' | 'chart') => void;
  setActiveView: React.Dispatch<React.SetStateAction<'table' | 'chart'>>;
  activeView: string;
  onAccountChange?: any;
  onTimeFrameChange?: (timeFrame: TimeFrameAPI) => void;
  onRegionChange: (region: Region | null) => void;
  onServiceChange: (service: Service | null) => void;
  services: Service[];
  isExcelDownloading: boolean;
}

const ServiceReportHeader: React.FC<ServiceReportHeaderProps> = ({
  downloadCsvHandler,
  // onViewChange,
  onTimeFrameChange,
  onRegionChange,
  onServiceChange,
  onAccountChange,
  activeView,
  setActiveView,
  services,
  isExcelDownloading,
}) => {
  // State for selected values
  const selectedAccountInfo = useSelector(accountState);
  const [timeFrame, setTimeFrame] = useState<TimeFrameDisplay>('Daily');
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const cloudProvider = selectedAccountInfo?.selectedAccount?.CloudProvider.toUpperCase();
  // Handle time frame selection
  const handleTimeFrameClick = (newTimeFrame: TimeFrameDisplay) => {
    setTimeFrame(newTimeFrame);
    if (onTimeFrameChange) {
      onTimeFrameChange(timeFrameMapping[newTimeFrame]);
    }
  };

  // Handle region change
  const handleRegionChange = (
    _event: React.ChangeEvent<Record<string, unknown>>,
    value: string | null
  ) => {
    const selectedRegion = value;
    setSelectedRegion(selectedRegion);
    if (onRegionChange) {
      onRegionChange(selectedRegion);
    }
  };

  // Handle service change
  const handleServiceChange = (
    _event: React.ChangeEvent<Record<string, unknown>>,
    value: string | null
  ) => {
    const selectedService = value;
    setSelectedService(selectedService);
    if (onServiceChange) {
      onServiceChange(selectedService);
    }
  };

  // Handle view change
  const handleViewChange = (view: 'table' | 'chart') => {
    setActiveView(view);
  };

  const timeFrameMapping: Record<TimeFrameDisplay, TimeFrameAPI> = {
    Daily: 'day',
    Weekly: 'week',
    Monthly: 'month',
    // 'Custom': 'custom'
  };

  return (
    <div className="serviceReportWrapper">
      <Grid className="inventoryReportsHeading ">Service Report</Grid>
      <div className="scrollingServiceReportHeader">
        <div className="serviceReportActions">
          <CustomTooltip
            title={
              isExcelDownloading
                ? 'Download in progress...'
                : 'Export current view as CSV'
            }
          >
            <Button
              className="exportButton"
              onClick={isExcelDownloading ? undefined : downloadCsvHandler}
              style={{
                pointerEvents: isExcelDownloading ? 'none' : 'auto',
                opacity: isExcelDownloading ? 0.6 : 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  gap: '4px',
                }}
              >
                {isExcelDownloading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  <>
                    <Excel />
                    <Download style={{ fontSize: '1.5rem' }} />
                  </>
                )}
              </div>
            </Button>
          </CustomTooltip>

          <ButtonGroup className="viewToggleGroup">
            <Button
              className={`timeframeButton ${
                activeView === 'table' ? 'timeframeActive' : ''
              }`}
              onClick={() => handleViewChange('table')}
            >
              <TableChartOutlinedIcon />
            </Button>
            <Button
              className={`timeframeButton ${
                activeView === 'chart' ? 'timeframeActive' : ''
              }`}
              onClick={() => handleViewChange('chart')}
            >
              <BarChartOutlinedIcon />
            </Button>
          </ButtonGroup>

          <div className="timeframeContainer">
            {([
              'Daily',
              'Weekly',
              'Monthly',
              // 'Custom'
            ] as TimeFrameDisplay[]).map((option) => (
              <Button
                key={option}
                className={`timeframeButton ${
                  timeFrame === option ? 'timeframeActive' : ''
                }`}
                onClick={() => handleTimeFrameClick(option)}
              >
                {option}
              </Button>
            ))}
          </div>

          <FormControl className="selectControl">
            <Autocomplete
              id="region-autocomplete"
              options={
                cloudProvider === CLOUD_PROVIDER_SERVICES.AWS
                  ? awsRegions
                  : azureLocations
              }
              value={selectedRegion || undefined}
              onChange={handleRegionChange}
              ListboxProps={{
                style: {
                  minWidth: '136px',
                  maxWidth: 'max-content',
                  whiteSpace: 'nowrap',
                  background: 'white',
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Region"
                  variant="outlined"
                  className="autocompleteInput"
                />
              )}
              PaperComponent={(props) => (
                <Paper {...props} className="autocompletePaper" />
              )}
              // popupIcon={<span className="autocompleteArrow">▼</span>}
              className="selectDropdown"
            />
          </FormControl>

          <FormControl className="selectControl">
            <Autocomplete
              id="service-autocomplete"
              options={services}
              value={selectedService || undefined}
              onChange={handleServiceChange}
              ListboxProps={{
                style: {
                  width: 'max-content',
                  whiteSpace: 'nowrap',
                  background: 'white',
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Services"
                  variant="outlined"
                  className="autocompleteInput"
                />
              )}
              PaperComponent={(props) => (
                <Paper {...props} className="autocompletePaper" />
              )}
              // popupIcon={<span className="autocompleteArrow">▼</span>}
              className="selectDropdown"
            />
          </FormControl>
          <SelectedAccount onAccountChange={onAccountChange} />
        </div>
      </div>
    </div>
  );
};

export default ServiceReportHeader;
